import React, { useRef, useState } from 'react'
import { useSize } from '../'
import { scaleLinear } from 'd3-scale'
import { line } from 'd3-shape'
import './styles.scss'
import { Tooltip } from '../'
import { XTicks } from '../'
import { YTicks } from '../'
import { Value } from '../'

type AnchorType = 'start' | 'end' | 'middle'
type ChartProps = {
  data: BasicDataType[][]
  yDomain: number[]
  xDomain: Date[]
  className: string
  yTicks?: { text?: number[]; lines?: number[] }
  xTicks?: { text?: string[]; lines?: string[] }
  height: number
  unit?: string
  annotations?: { d: string; v: number; t: string }[][]
}
export const MultiLine: React.FC<ChartProps> = ({
  data,
  height = 40,
  yDomain,
  xDomain,
  className,
  yTicks,
  xTicks,
  unit,
  annotations
}) => {


  const yFunction = (d: any) =>
    yAxisScale.range()[0] + yAxisScale.range()[1] - yAxisScale(d.v)

  const xFunction = (d: any, i: number) => {
    return xAxisScale(new Date(d.d))
  }
  const [container, size] = useSize()

  const [ttData, setTtData] = useState<
    | {
        pos: { x: number; anchor: AnchorType }
        data?: { d: string; v: number }[]
      }
    | undefined
  >()

  const createTooltip = e => {
    let xgraph = xAxisScale.invert(Math.floor(e.nativeEvent.layerX))
    let date = new Date(xgraph).toISOString().split('T')[0]

    let els = data.map(d => d.find(f => f.d === date)).filter(f => f)

    if (els.length === 0) return null

    const x = xAxisScale(new Date(date))
    let ttdata = {
      pos: {
        x: x,
        anchor: (x > size.width / 2 ? 'end' : 'start') as AnchorType
      },
      data: els
    }
    setTtData(ttdata)
  }
  const cancelTooltip = () => setTtData(undefined)

  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const gutterTop = 0
  const gutterRight = 40
  const gutterLeft = 0
  const gutterBottom = 20
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size, xDomain]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size, xDomain]
  )
  const lineFunction = React.useMemo(
    () =>
      line()
        .x(xFunction)
        .y(yFunction),
    [size, xDomain]
  )
  return (
    <div className={'multiline ' + className}>
      <div
        ref={container}
        style={{ position: 'relative', width: '100%', height: height }}
      >
        <svg
          // style={{ colorScheme: 'dark' }}
          ref={svg}
          height={height}
          onTouchMove={createTooltip}
          onMouseMove={createTooltip}
          onMouseLeave={cancelTooltip}
          onTouchCancel={cancelTooltip}
        >
          {xTicks && !ttData && (
            <XTicks data={xTicks} x={xAxisScale} y={yAxisScale} />
          )}
          {yTicks && (
            <YTicks
              data={yTicks}
              x={xAxisScale}
              y={yAxisScale}
              maxwidth={size.width}
            />
          )}
          {data.length > 0 &&
            data.map((d, i) => {
              let filtered = d
              return (
                <React.Fragment key={'Multiline_' + i}>
                  <path
                    d={lineFunction(filtered)}
                    className={'_' + i}
                    stroke='#fff'
                  />
                  {!ttData && (
                    <Value
                      value={filtered[filtered.length - 1].v}
                      x={xAxisScale(new Date(filtered[filtered.length - 1].d))}
                      y={yAxisScale}
                      offset={0}
                      textAnchor={'start'}
                      unit={unit}
                    />
                  )}
                </React.Fragment>
              )
            })}
          {annotations &&
            !ttData &&
            annotations.map((set, i) => {
              return (
                <g className={'annotation _' + i} key={'AnnotationSet_' + i}>
                  {set
                    .filter(
                      f =>
                        new Date(f.d) >= new Date(xDomain[0]) &&
                        new Date(f.d) <= new Date(xDomain[1])
                    )
                    .map((anno, i) => {
                      return (
                        <g
                          transform={`translate(${xAxisScale(
                            new Date(anno.d)
                          )},${yAxisScale.range()[0] - yAxisScale(anno.v)})`}
                          key={'Annotation_' + i}
                        >
                          <circle r={3} />
                          <text textAnchor='middle' y={-5}>
                            {anno.t}
                          </text>
                        </g>
                      )
                    })}
                </g>
              )
            })}
          {ttData && <Tooltip data={ttData} y={yAxisScale} />}
        </svg>
      </div>
    </div>
  )
}
